import React from 'react';
import { FormField, SwitchToggle } from 'src/shared/forms/components';
import type { WizardStepComponentProps } from 'src/shared/forms/types';
import type { ProductWizardData } from '../../../hooks/useProductWizard';
import { ShippersSection } from '../components/ShippersSection';
import { PreOrderSettingsSection } from '../components/PreOrderSettingsSection';

interface ShippingStepProps extends WizardStepComponentProps {
  data: ProductWizardData['shipping'];
}

const ShippingStep: React.FC<ShippingStepProps> = ({
  data,
  onDataChange,
  errors = []
}) => {
  // DEBUG: Log loaded shipping data
  React.useEffect(() => {
    console.log('=== ShippingStep Data Loaded ===');
    console.log('Shippers:', data.shippers);
    console.log('Dimensions:', data.dimensions);
    console.log('Pre-order:', data.preOrder);
    console.log('Is Dangerous:', data.isDangerousProduct);
  }, [data]);

  const updateShippingData = (updates: Partial<ProductWizardData['shipping']>) => {
    onDataChange({
      ...data,
      ...updates
    });
  };

  const updateDimensions = (updates: Partial<ProductWizardData['shipping']['dimensions']>) => {
    updateShippingData({
      dimensions: {
        weight: data.dimensions?.weight ?? 0,
        weightUnit: data.dimensions?.weightUnit ?? 'g',  // Default to grams
        length: data.dimensions?.length ?? 0,
        width: data.dimensions?.width ?? 0,
        height: data.dimensions?.height ?? 0,
        dimensionUnit: 'cm' as const,
        ...(data.dimensions || {}),
        ...updates
      }
    });
  };

  return (
    <div className="tw-space-y-6" data-testid="wizard-step-shipping">
      {/* Package Dimensions & Weight Group */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm" data-testid="wizard-shipping-dimensions-group">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900">
            Package Dimensions & Weight
          </h3>
          <p className="tw-text-sm tw-text-gray-500">
            Provide accurate package measurements for shipping
            calculations.
          </p>
        </div>
        <div className="tw-grid tw-gap-6">
          {/* Package Weight */}
          {/* Note: API stores weight in grams. Use convertWeightToGrams() from dataTransformation.ts when submitting */}
          <div className="tw-flex tw-flex-col tw-gap-2" data-testid="wizard-shipping-weight-input">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700">
              Package Weight{" "}
              <span className="tw-text-red-500">*</span>
            </label>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div className="tw-relative">
                <input
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-2.5 tw-pr-12 tw-text-gray-900 tw-placeholder-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                  placeholder="1.5"
                  type="number"
                  step="0.1"
                  min="0"
                  value={data.dimensions?.weight || ""}
                  onChange={(e) =>
                    updateDimensions({
                      weight: parseFloat(e.target.value) || 0,
                    })
                  }
                />
                <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-text-sm tw-text-gray-500">
                  <select
                    className="tw-bg-transparent tw-border-none tw-outline-none tw-text-gray-500"
                    value={data.dimensions?.weightUnit || 'g'}
                    onChange={(e) =>
                      updateDimensions({
                        weightUnit: e.target.value as "kg" | "g",
                      })
                    }
                  >
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                  </select>
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500">
                <span className="material-symbols-outlined tw-text-primary">
                  info
                </span>
                <span>Include packaging weight for accurate shipping cost</span>
              </div>
            </div>
            <p className="tw-text-xs tw-text-gray-500">
              Total weight including product and packaging materials.
            </p>
          </div>

          {/* Package Dimensions */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700">
              Package Dimensions{" "}
              <span className="tw-text-red-500">*</span>
            </label>
            <div className="tw-grid tw-grid-cols-3 tw-gap-4">
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                  Length (cm)
                </label>
                <input
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 tw-placeholder-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                  placeholder="30"
                  type="number"
                  min="0"
                  value={data.dimensions?.length || ""}
                  onChange={(e) =>
                    updateDimensions({
                      length: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                  Width (cm)
                </label>
                <input
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 tw-placeholder-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                  placeholder="20"
                  type="number"
                  min="0"
                  value={data.dimensions?.width || ""}
                  onChange={(e) =>
                    updateDimensions({
                      width: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                  Height (cm)
                </label>
                <input
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 tw-placeholder-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                  placeholder="10"
                  type="number"
                  min="0"
                  value={data.dimensions?.height || ""}
                  onChange={(e) =>
                    updateDimensions({
                      height: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
            <p className="tw-text-xs tw-text-gray-500">
              Outer package dimensions for shipping calculations.
            </p>
          </div>

          {/* Dangerous Item */}
          <div className="tw-flex tw-flex-col tw-gap-4" data-testid="wizard-shipping-dangerousToggle">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700">
              Special Handling
            </label>
            <SwitchToggle
              title="Is Dangerous Item?"
              description="Requires special shipping handling (fragile, hazmat, oversized, etc.)"
              checked={data.isDangerousProduct || false}
              onChange={(checked) =>
                updateShippingData({
                  isDangerousProduct: checked,
                })
              }
              icon="warning"
              iconColor="tw-text-orange-500"
              variant="warning"
            />
          </div>
        </div>
      </div>

      {/* Shipping Shippers & Services Group */}
      <div data-testid="wizard-shipping-shippers-section">
        <ShippersSection
          shippers={data.shippers}
          onShippersChange={(shippers) =>
            updateShippingData({ shippers })
          }
        />
      </div>

      {/* Pre-Order Settings Group */}
      <div data-testid="wizard-shipping-preorder-section">
        <PreOrderSettingsSection
          preOrder={data.preOrder}
          onPreOrderChange={(preOrder) =>
            updateShippingData({ preOrder })
          }
        />
      </div>
    </div>
  );
};

export default ShippingStep;